<template>
  <div class="table-of-contents">
    <h3>Table of Contents</h3>
    <ul>
      <li v-for="(section, index) in sections" :key="index">
        <a
          :class="{ active: activeSection === section.link }"
          @click.prevent="scrollToSection(section.link)"
        >
          {{ section.title }}
        </a>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'TableOfContents',
  props: {
    sections: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      activeSection: null, // Track the currently active section
      observer: null, // Store the IntersectionObserver instance
    };
  },
  mounted() {
    this.setupIntersectionObserver();
  },
  beforeUnmount() {
    // Clean up the observer when the component is destroyed
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    scrollToSection(link) {
      // Set the active section
      this.activeSection = link;

      // Find the target element by ID (remove the '#' from the link)
      const targetId = link.substring(1);
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        // Get the position of the target element relative to the document
        const elementPosition =
          targetElement.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - 150; // Adjust for navbar height (e.g., 60px)

        // Scroll to the adjusted position to ensure the top of the section is visible
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    },
    setupIntersectionObserver() {
      // Create an IntersectionObserver to watch for sections entering the viewport
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // When a section enters the viewport, set it as the active section
              const sectionId = entry.target.id;
              this.activeSection = `#${sectionId}`; // Match the link format (e.g., "#the-challenge")
            }
          });
        },
        {
          root: null, // Use the viewport as the root
          rootMargin: '-100px 0px -50% 0px', // Adjust for navbar height and trigger point
          threshold: 0.1, // Trigger when 10% of the section is visible
        }
      );

      // Observe each section by its ID
      this.sections.forEach((section) => {
        const sectionId = section.link.substring(1); // Remove the '#' to get the ID
        const element = document.getElementById(sectionId);
        if (element) {
          this.observer.observe(element);
        }
      });
    },
  },
};
</script>

<style scoped>
.table-of-contents {
  margin-top: 3rem;
  width: 300px;
  /* background: #f5faff; */
  background-color: #ffffff;
  padding: 20px;
  border-radius: 10px;
  position: sticky;
  /* box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); */
  top: 140px; /* Adjust based on navbar height (e.g., 60px) */
  align-self: flex-start;
  min-height: 300px; /* Ensure consistent height for border positioning */
  box-sizing: border-box;
  margin-bottom: 4rem;
  border: 1px solid #dddddd;
}

h3 {
  font-size: 22px;
  margin-bottom: 15px;
  text-align: left;
  position: relative;
  z-index: 2;
}

ul {
  list-style: none;
  padding: 0;
  position: relative;
  z-index: 2; /* Ensure list items are above the border */
}

li {
  margin-bottom: 15px;
}

a {
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  display: block;
  text-align: left;
  position: relative; /* For positioning the underline */

  cursor: pointer; /* Show pointer cursor on hover */
  transition: color 0.3s ease; /* Smooth transition for text color change */
}

a:hover {
  text-decoration: none; /* Remove default underline on hover */
}

a.active {
  color: #33b0f8; /* Blue text color for active link */
}

@media (max-width: 800px) {
  .table-of-contents {
    display: none;
    width: 100%;
    position: static;
    margin-top: 20px;
    top: 0; /* Reset sticky positioning on mobile */
  }

  .table-of-contents::before {
    display: none; /* Hide the border on mobile */
  }
}
</style>
